import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

export default function PastStudentsNotGraduatedNotifications() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/admin/registrations/past-students-not-graduated');
        if (!cancelled) {
          const payload = response.data || {};
          setStudents(Array.isArray(payload.students) ? payload.students : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load past students not graduated', err);
          setError('Failed to load past students not yet graduated. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((student) => {
      if (batchStatusFilter !== 'all' && student.batchStatus !== batchStatusFilter) {
        return false;
      }

      if (!term) return true;

      const haystack = [
        student.studentName,
        student.studentId,
        student.nicNumber,
        student.registrationNumber,
        student.courseName,
        student.courseShortName,
        student.batchName,
        student.batchInternalName,
        student.whatsappGroupName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [students, search, batchStatusFilter]);

  const totalCount = students.length;
  const visibleCount = filteredStudents.length;

  const handleDownloadExcel = async () => {
    if (!filteredStudents.length) {
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const headers = [
        'Student ID',
        'Student Name',
        'NIC',
        'DOB',
        'Telephone',
        'Email',
        'Address',
        'Registration Number',
        'Registration Status',
        'University Register Date',
        'Course Name',
        'Course Short Name',
        'Batch Name',
        'Batch Internal Name',
        'Batch Starting Date',
        'Batch Ending Date',
        'WhatsApp Group',
        'Batch Status',
      ];

      const rows = filteredStudents.map((student) => [
        student.studentId || '',
        student.studentName || '',
        student.nicNumber || '',
        student.dob || '',
        student.telephone || '',
        student.email || '',
        student.address || '',
        student.registrationNumber || '',
        student.registrationStatus || '',
        student.universityRegisterDate || '',
        student.courseName || '',
        student.courseShortName || '',
        student.batchName || '',
        student.batchInternalName || '',
        student.batchStartingDate || '',
        student.batchEndingDate || '',
        student.whatsappGroupName || '',
        student.batchStatus || '',
      ]);

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Past Not Graduated');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Past_Students_Not_Graduated_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download past students not graduated Excel', err);
    }
  };

  return (
    <DashboardLayout title="Past Students Not Yet Graduated">
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-gray-900">Past Students Not Yet Graduated</h1>
          <p className="text-sm text-gray-600">
            This view lists students whose batches have reached delivery completion, but their registration delivery status is not yet
            updated (for example, not marked as graduated or certificate completed).
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">{error}</div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 border border-purple-100">
              Total students with pending delivery status: <span className="ml-1 font-semibold">{totalCount}</span>
            </span>
            {totalCount > 0 && (
              <span className="text-xs text-gray-500">Showing {visibleCount} of {totalCount}</span>
            )}
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end sm:justify-end sm:gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="batch-status-filter">
                Filter by batch status
              </label>
              <select
                id="batch-status-filter"
                value={batchStatusFilter}
                onChange={(e) => setBatchStatusFilter(e.target.value)}
                className="w-full sm:w-48 rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                <option value="all">All statuses</option>
                <option value="delivery completed">Delivery completed</option>
                <option value="batch completed">Batch completed</option>
              </select>
            </div>
            <div className="w-full sm:w-64">
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="past-students-search">
                Search by student, registration, batch or course
              </label>
              <input
                id="past-students-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search..."
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
            {visibleCount > 0 && (
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download as Excel
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-600">Loading past students not yet graduated...</div>
        ) : visibleCount === 0 ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            No students currently match the criteria (batch delivery completed or batch completed with delivery status not set).
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Student</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Contact</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Registration</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Batch & Course</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Batch status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredStudents.map((student) => (
                  <tr key={student.registrationId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 align-top">
                      <div className="font-medium text-gray-900">{student.studentName || 'N/A'}</div>
                      <div className="text-xs text-gray-500">ID: {student.studentId || 'N/A'}</div>
                      {student.nicNumber && (
                        <div className="text-xs text-gray-500">NIC: {student.nicNumber}</div>
                      )}
                      {student.dob && (
                        <div className="text-xs text-gray-500">DOB: {student.dob}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      {student.telephone && <div>Tel: {student.telephone}</div>}
                      {student.email && <div className="truncate">Email: {student.email}</div>}
                      {student.address && <div className="mt-1 line-clamp-2">{student.address}</div>}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">{student.registrationNumber}</div>
                      <div className="mt-1 inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                        {student.registrationStatus}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">Delivery status: Not set</div>
                      {student.universityRegisterDate && (
                        <div className="mt-1 text-[11px] text-gray-500">
                          University registered: {student.universityRegisterDate}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-gray-700">
                      <div className="font-medium text-gray-900">
                        {student.courseShortName || student.courseName || 'N/A'}
                      </div>
                      <div className="text-[11px] text-gray-500">{student.courseName}</div>
                      <div className="mt-1 text-[11px] text-gray-700">
                        Batch: {student.batchName || student.batchInternalName || 'N/A'}
                      </div>
                      {student.batchInternalName && (
                        <div className="text-[11px] text-gray-500">Internal: {student.batchInternalName}</div>
                      )}
                      {student.batchStartingDate && (
                        <div className="text-[11px] text-gray-500">Started: {student.batchStartingDate}</div>
                      )}
                      {student.batchEndingDate && (
                        <div className="text-[11px] text-gray-500">Ended: {student.batchEndingDate}</div>
                      )}
                      {student.whatsappGroupName && (
                        <div className="text-[11px] text-gray-500">WhatsApp: {student.whatsappGroupName}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top">
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 border border-amber-200">
                        {student.batchStatus || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
